import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
  Tooltip as RechartTooltip, Legend,
} from 'recharts';
import { PageHeader, StatCard, SubTabBar, fmtDollar, fmtPct, fmtCount, fmtMillions } from './shared';
import { apiFetch } from '../api';

const SCENARIO_COLORS = {
  'Conservative': '#6c757d',
  'Base Case':    '#0067a0',
  'Optimistic':   '#198754',
};

function fmtPIVal(v, fmt) {
  if (fmt === 'dollar')  return fmtDollar(v);
  if (fmt === 'pct1')    return typeof v === 'number' ? `${v.toFixed(1)}%` : v;
  if (fmt === 'rate1')   return typeof v === 'number' ? v.toFixed(1) : v;
  if (fmt === 'ratio2')  return typeof v === 'number' ? v.toFixed(2) : v;
  if (fmt === 'count')   return typeof v === 'number' ? fmtCount(v) : v;
  return v;
}

function fmtPIVar(v, fmt) {
  const sign = v > 0 ? '+' : '';
  if (fmt === 'dollar') return (v > 0 ? '+' : '') + fmtDollar(v);
  if (fmt === 'pp')     return `${sign}${v}pp`;
  return `${sign}${v}`;
}

function fmtSignedDollar(v) { return (v > 0 ? '+' : '') + fmtDollar(v); }

function progStatusClass(s) {
  return s === 'ON TRACK' ? 'status-on-track' : s === 'EXCEEDING' ? 'status-exceeding' : 'status-at-risk';
}

function DrillSparkline({ data, dir }) {
  const w = 60, h = 22;
  const min = Math.min(...data), max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * (w - 4) + 2;
    const y = h - 2 - ((v - min) / range) * (h - 4);
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <polyline points={pts} fill="none" stroke={dir === 'FAV' ? '#198754' : '#dc3545'} strokeWidth={1.5} strokeLinejoin="round" />
    </svg>
  );
}

function LoadingRow() {
  return <div className="p-4 text-muted">Loading…</div>;
}

function ErrorRow() {
  return <div className="p-4 text-danger">Failed to load dashboard data.</div>;
}

// ── Views ─────────────────────────────────────────────────────────────────────

function VBCDashboardView({ memberCount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiFetch('/api/dashboards/performance-intelligence/vbc')
      .then(r => r.json())
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return <ErrorRow />;
  if (!data)  return <LoadingRow />;

  return (
    <>
      <PageHeader title="Performance Intelligence  ·  VBC Program Dashboard" memberCount={memberCount} />
      <div className="dash-stats-row">
        {data.kpis.map((k, i) => <StatCard key={i} {...k} />)}
      </div>

      <div className="card">
        <div className="card-header fw-semibold">VBC Program Performance Scorecard — All Active Programs</div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-sm mb-0 dash-table">
              <thead>
                <tr><th>Program Name</th><th>Type</th><th>Attr. Lives</th><th>Baseline PMPM</th><th>Actual PMPM</th><th>Savings / Loss</th><th>Quality</th><th>Risk Adj.</th><th>Status</th></tr>
              </thead>
              <tbody>
                {data.vbcPrograms.map(r => (
                  <tr key={r.name}>
                    <td className="fw-semibold">{r.name}</td>
                    <td>{r.type}</td><td>{fmtCount(r.lives)}</td><td>{fmtDollar(r.baseline)}</td><td>{fmtDollar(r.actual)}</td>
                    <td className={r.savings < 0 ? 'text-danger fw-semibold' : 'text-success fw-semibold'}>{fmtMillions(r.savings)}</td>
                    <td>{fmtPct(r.quality)}</td><td>{r.risk}</td>
                    <td><span className={`dash-status-badge ${progStatusClass(r.status)}`}>{r.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="row g-3 mt-0">
        <div className="col-12 col-xl-7">
          <div className="card mt-3">
            <div className="card-header fw-semibold">Savings Trend vs. Target — Rolling Quarters ($M)</div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.savingsTrend.map(d => ({ ...d, met: Math.min(d.actual, d.target), over: Math.max(0, d.actual - d.target) }))} margin={{ left: 0, right: 20, top: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="quarter" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `$${v}M`} />
                  <RechartTooltip formatter={(v, name) => [`$${v}M`, name]} />
                  <Legend />
                  <Bar dataKey="met"  name="Savings (Met Target)"   stackId="s" fill="#adb5bd" />
                  <Bar dataKey="over" name="Savings (Above Target)" stackId="s" fill="#198754" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div className="col-12 col-xl-5">
          <div className="card mt-3">
            <div className="card-header fw-semibold">Risk Adjustment Sensitivity Analysis</div>
            <div className="card-body">
              <p className="mb-3" style={{ fontSize: '0.8rem', color: 'var(--eh-text-mid)' }}>Savings impact under different risk adjustment scenarios:</p>
              <div className="d-flex gap-3">
                {data.riskAdjScenarios.map(s => (
                  <div key={s.label} style={{
                    flex: 1, textAlign: 'center', borderRadius: 8, padding: '1rem 0.75rem',
                    border: s.current ? '2px solid var(--eh-navy)' : '1px solid #dee2e6',
                    background: s.current ? '#e8f1f8' : '#fff',
                  }}>
                    <div style={{ fontSize: '0.76rem', color: 'var(--eh-text-mid)', marginBottom: '0.5rem' }}>{s.label}</div>
                    <div style={{ fontSize: '1.8rem', fontWeight: 700, color: 'var(--eh-navy)', lineHeight: 1.1 }}>{fmtMillions(s.value)}</div>
                    {s.current && <span className="dash-current-badge mt-2 d-inline-block">Current Estimate</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function ControlGroupEvalView({ memberCount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiFetch('/api/dashboards/performance-intelligence/control')
      .then(r => r.json())
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return <ErrorRow />;
  if (!data)  return <LoadingRow />;

  return (
    <>
      <PageHeader title="Performance Intelligence  ·  Program Evaluation vs. Control Group" memberCount={memberCount} />
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="d-flex align-items-center gap-2 flex-wrap" style={{ fontSize: '0.82rem' }}>
            <span className="fw-bold" style={{ color: 'var(--eh-navy)' }}>Program: SPECIALTY TCOC BH — Market A</span>
            {data.contextChips.map(t => (
              <span key={t} className="dash-filter-tag">{t}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="dash-stats-row">
        {data.kpis.map((k, i) => <StatCard key={i} {...k} />)}
      </div>

      <div className="card mb-3" style={{ borderLeft: '4px solid #198754' }}>
        <div className="card-body py-3">
          <div style={{ fontSize: '0.75rem', color: 'var(--eh-text-mid)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Net Program Effect (PMPM)</div>
          <div className="d-flex align-items-baseline gap-3 flex-wrap mt-1">
            <div style={{ fontSize: '2.2rem', fontWeight: 700, color: '#198754' }}>{fmtDollar(data.netEffect.pmpm)}</div>
            <div style={{ fontSize: '0.88rem', color: 'var(--eh-text-mid)' }}>{data.netEffect.description}</div>
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--eh-text-mid)', marginTop: 4 }}>
            {data.netEffect.pValue} · {data.netEffect.ci}
          </div>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-12 col-xl-5">
          <div className="card">
            <div className="card-header fw-semibold">Outcome Comparison — Program vs. Matched Control</div>
            <div className="card-body p-0">
              <table className="table table-sm mb-0 dash-table">
                <thead><tr><th>Metric</th><th>Program</th><th>Control</th><th>Diff</th><th>Status</th></tr></thead>
                <tbody>
                  {data.controlOutcomes.map(r => (
                    <tr key={r.metric}>
                      <td className="fw-semibold">{r.metric}</td>
                      <td>{fmtPIVal(r.program, r.valFmt)}</td>
                      <td>{fmtPIVal(r.control, r.valFmt)}</td>
                      <td className={r.dir === 'FAV' ? 'text-success fw-semibold' : 'text-danger fw-semibold'}>{fmtPIVar(r.diff, r.diffFmt)}</td>
                      <td><span className={`dash-status-badge ${r.dir === 'FAV' ? 'status-on-track' : 'status-at-risk'}`}>{r.dir}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card mt-3">
            <div className="card-header fw-semibold">PSM Quality Checks</div>
            <div className="card-body p-0">
              {data.psmChecks.map(r => (
                <div key={r.label} className="d-flex justify-content-between px-3 py-2 border-bottom">
                  <span style={{ fontSize: '0.82rem', color: 'var(--eh-text-mid)' }}>{r.label}</span>
                  <span className="fw-semibold" style={{ fontSize: '0.82rem', color: r.value.includes('✓') ? '#198754' : 'inherit' }}>{r.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="col-12 col-xl-7">
          <div className="card">
            <div className="card-header fw-semibold">Key Metric Drivers — Program vs. Matched Control Group</div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm mb-0 dash-table" style={{ fontSize: '0.78rem' }}>
                  <thead>
                    <tr><th>Metric</th><th>Category</th><th>Program</th><th>Control</th><th>Diff</th><th>PMPM Impact</th><th>Direction</th><th>Trend</th></tr>
                  </thead>
                  <tbody>
                    {data.controlDrivers.map(r => (
                      <tr key={r.metric}>
                        <td className="fw-semibold">{r.metric}</td>
                        <td style={{ color: 'var(--eh-text-mid)' }}>{r.cat}</td>
                        <td>{r.prog}</td><td>{r.ctrl}</td>
                        <td className={r.dir === 'FAV' ? 'text-success fw-semibold' : 'text-danger fw-semibold'}>{fmtPIVar(r.diff, r.diffFmt)}</td>
                        <td className={r.dir === 'FAV' ? 'text-success fw-semibold' : 'text-danger fw-semibold'}>{fmtSignedDollar(r.impact)}</td>
                        <td><span className={`dash-status-badge ${r.dir === 'FAV' ? 'status-on-track' : 'status-at-risk'}`}>{r.dir}</span></td>
                        <td style={{ fontSize: '0.72rem', color: 'var(--eh-text-mid)' }}>{r.trend}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-3 py-2" style={{ fontSize: '0.75rem', color: 'var(--eh-text-mid)' }}>
                {data.netEffect.footer}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function ProgramDrillDownView({ memberCount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiFetch('/api/dashboards/performance-intelligence/drilldown')
      .then(r => r.json())
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return <ErrorRow />;
  if (!data)  return <LoadingRow />;

  return (
    <>
      <PageHeader title="Performance Intelligence  ·  Program Drill-Down: Cost & Quality vs. Target" memberCount={memberCount} />
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="d-flex align-items-center gap-2 flex-wrap" style={{ fontSize: '0.82rem' }}>
            <span className="fw-bold" style={{ color: 'var(--eh-navy)' }}>Program: BH TCOC SPECIALTY — Market A</span>
            {data.contextChips.map(t => (
              <span key={t} className="dash-filter-tag">{t}</span>
            ))}
          </div>
        </div>
      </div>
      <div className="row g-3 mb-3">
        <div className="col-12 col-md-6">
          <div className="card" style={{ borderLeft: '4px solid #198754' }}>
            <div className="card-body py-3">
              <div style={{ fontSize: '0.75rem', color: 'var(--eh-text-mid)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cost Performance vs. Target</div>
              <div className="d-flex align-items-baseline gap-3 mt-1 flex-wrap">
                <div>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--eh-navy)', lineHeight: 1 }}>{fmtDollar(data.costCard.actual)}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--eh-text-mid)' }}>Actual PMPM</div>
                </div>
                <div style={{ fontSize: '0.82rem', color: 'var(--eh-text-mid)' }}>vs.</div>
                <div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 600, color: '#6c757d' }}>{fmtDollar(data.costCard.target)}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--eh-text-mid)' }}>Target PMPM</div>
                </div>
              </div>
              <div className="mt-2 fw-bold" style={{ color: '#198754' }}>{data.costCard.summary}</div>
              <div className="mt-2">
                <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.72rem', color: 'var(--eh-text-mid)' }}>
                  <span>Cost vs. Target</span><span>{data.costCard.pct}%</span>
                </div>
                <div style={{ background: '#e9ecef', borderRadius: 4, height: 8 }}>
                  <div style={{ width: `${data.costCard.pct}%`, height: '100%', background: '#198754', borderRadius: 4 }} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6">
          <div className="card" style={{ borderLeft: '4px solid #0067a0' }}>
            <div className="card-body py-3">
              <div style={{ fontSize: '0.75rem', color: 'var(--eh-text-mid)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Quality Performance vs. Target</div>
              <div className="d-flex align-items-baseline gap-3 mt-1 flex-wrap">
                <div>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--eh-navy)', lineHeight: 1 }}>{data.qualityCard.actual}%</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--eh-text-mid)' }}>Actual Quality Score</div>
                </div>
                <div style={{ fontSize: '0.82rem', color: 'var(--eh-text-mid)' }}>vs.</div>
                <div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 600, color: '#6c757d' }}>{data.qualityCard.target}%</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--eh-text-mid)' }}>Quality Target</div>
                </div>
              </div>
              <div className="mt-2 fw-bold" style={{ color: '#0067a0' }}>{data.qualityCard.summary}</div>
              <div className="mt-2">
                <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.72rem', color: 'var(--eh-text-mid)' }}>
                  <span>Quality Score</span><span>{data.qualityCard.actual}% <span style={{ color: '#6c757d' }}>/ {data.qualityCard.target}% target</span></span>
                </div>
                <div style={{ background: '#e9ecef', borderRadius: 4, height: 8, position: 'relative' }}>
                  <div style={{ width: `${data.qualityCard.pct}%`, height: '100%', background: '#0067a0', borderRadius: 4 }} />
                  <div style={{ position: 'absolute', left: `${data.qualityCard.targetPct}%`, top: -2, bottom: -2, width: 2, background: '#6c757d', borderRadius: 1 }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-header fw-semibold">Key Metric Drivers — Favorable & Unfavorable vs. Target</div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-sm mb-0 dash-table">
              <thead>
                <tr><th>Metric</th><th>Category</th><th>Actual</th><th>Target</th><th>Variance</th><th>PMPM Impact</th><th>Trend (6 mo)</th><th>Direction</th></tr>
              </thead>
              <tbody>
                {data.drilldownMetrics.map(r => (
                  <tr key={r.metric}>
                    <td className="fw-semibold">{r.metric}</td>
                    <td style={{ fontSize: '0.75rem', color: 'var(--eh-text-mid)' }}>{r.cat}</td>
                    <td>{fmtPIVal(r.actual, r.valFmt)}</td>
                    <td>{fmtPIVal(r.target, r.valFmt)}</td>
                    <td className={r.dir === 'FAV' ? 'text-success fw-semibold' : 'text-danger fw-semibold'}>{fmtPIVar(r.variance, r.varFmt)}</td>
                    <td><span className={`dash-status-badge ${r.dir === 'FAV' ? 'status-on-track' : 'status-at-risk'}`}>{r.dir}</span></td>
                    <td className={r.dir === 'FAV' ? 'text-success fw-semibold' : 'text-danger fw-semibold'}>{fmtSignedDollar(r.impact)}</td>
                    <td><DrillSparkline data={r.trend6mo} dir={r.dir} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-2" style={{ fontSize: '0.78rem', color: 'var(--eh-text-mid)' }}>
            {data.drilldownFooter}
          </div>
        </div>
      </div>
    </>
  );
}

function ContractSettingView({ memberCount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [levers, setLevers] = useState({ trend: 3.0, riskAdj: 1.00, qualityGate: 88, msr: 2.0, savingsRate: 50 });
  const upd = key => e => setLevers(p => ({ ...p, [key]: parseFloat(e.target.value) }));

  useEffect(() => {
    apiFetch('/api/dashboards/performance-intelligence/contract')
      .then(r => r.json())
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return <ErrorRow />;
  if (!data)  return <LoadingRow />;

  return (
    <>
      <PageHeader title="Performance Intelligence  ·  VBC Baseline & Target Contract Setting" memberCount={memberCount} />
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-2">
              <button className="btn btn-sm btn-primary w-100">Load Data</button>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-12 col-xl-7">
          <div className="card">
            <div className="card-header fw-semibold">Baseline Summary — Historical Actuals (2022–2025)</div>
            <div className="card-body p-0">
              <table className="table table-sm mb-0 dash-table">
                <thead><tr><th>Metric</th><th>2022</th><th>2023</th><th>2024</th><th>2025 Baseline</th><th>Trend</th></tr></thead>
                <tbody>
                  {data.baselineSummary.map(r => {
                    const fmtBL = v => fmtPIVal(v, r.valFmt);
                    return (
                      <tr key={r.metric}>
                        <td className="fw-semibold">{r.metric}</td>
                        <td>{fmtBL(r.y22)}</td><td>{fmtBL(r.y23)}</td><td>{fmtBL(r.y24)}</td>
                        <td className="fw-semibold">{fmtBL(r.y25)}</td>
                        <td className={r.trend.startsWith('▲') ? 'text-danger fw-semibold' : 'text-success fw-semibold'}>{r.trend}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card mt-3">
            <div className="card-header fw-semibold">Benchmark Comparison — O/E vs. Peer Group</div>
            <div className="card-body p-0">
              <table className="table table-sm mb-0 dash-table">
                <thead><tr><th>Metric</th><th>Actual</th><th>Peer Avg</th><th>O/E</th><th>Position</th></tr></thead>
                <tbody>
                  {data.benchmarkComparison.map(r => (
                    <tr key={r.metric}>
                      <td className="fw-semibold">{r.metric}</td>
                      <td>{fmtPIVal(r.actual, r.valFmt)}</td>
                      <td>{fmtPIVal(r.peerAvg, r.valFmt)}</td>
                      <td className={r.oe > 1 ? 'text-danger fw-semibold' : 'text-success fw-semibold'}>{r.oe}</td>
                      <td>{r.position}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="col-12 col-xl-5">
          <div className="card">
            <div className="card-header fw-semibold">Simulation Levers — Adjust to Model Contract Terms</div>
            <div className="card-body">
              <div className="sim-levers d-flex flex-column gap-1">
                {[
                  { label: 'Trend Factor (Annual %)', key: 'trend',       min: 1.0,  max: 5.0,  step: 0.25, fmt: v => `+${v.toFixed(2)}%`  },
                  { label: 'Risk Adjustment Factor',  key: 'riskAdj',     min: 0.85, max: 1.15, step: 0.01, fmt: v => v.toFixed(2)          },
                  { label: 'Quality Gate Threshold',  key: 'qualityGate', min: 80,   max: 95,   step: 1,    fmt: v => `${v}%`               },
                  { label: 'Min. Savings Rate (MSR)', key: 'msr',         min: 0.5,  max: 5.0,  step: 0.5,  fmt: v => `${v}%`               },
                  { label: 'Shared Savings Rate',     key: 'savingsRate', min: 30,   max: 70,   step: 5,    fmt: v => `${v}%`               },
                ].map(({ label, key, min, max, step, fmt }) => (
                  <div key={key} className="d-flex align-items-center gap-2">
                    <label style={{ fontSize: '0.75rem', color: 'var(--eh-text-mid)', width: 148, flexShrink: 0 }}>{label}</label>
                    <input type="range" className="form-range" style={{ flex: 1, marginBottom: 0 }} min={min} max={max} step={step} value={levers[key]} onChange={upd(key)} />
                    <span className="fw-semibold" style={{ fontSize: '0.78rem', color: 'var(--eh-navy)', width: 46, textAlign: 'right', flexShrink: 0 }}>{fmt(levers[key])}</span>
                  </div>
                ))}
                <div className="d-flex align-items-center gap-2">
                  <label className="mb-0" style={{ fontSize: '0.75rem', color: 'var(--eh-text-mid)', width: 148, flexShrink: 0 }}>Performance Period</label>
                  <select className="form-select form-select-sm" style={{ flex: 1 }}>
                    <option>CY 2026</option>
                    <option>CY 2027</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="card mt-3">
            <div className="card-header fw-semibold">Scenario Comparison</div>
            <div className="card-body p-0">
              {data.scenarioComparison.map(s => {
                const color = SCENARIO_COLORS[s.scenario];
                return (
                  <div key={s.scenario} className="px-3 py-2 border-bottom"
                    style={s.current ? { background: '#e8f5fb' } : {}}>
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <div className="fw-semibold" style={{ fontSize: '0.82rem', color }}>
                        {s.scenario}{s.current && <span className="ms-2 badge" style={{ background: color, fontSize: '0.65rem' }}>SELECTED</span>}
                      </div>
                      <div className="text-end">
                        <div className="fw-bold text-success">{fmtMillions(s.savings)}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--eh-text-mid)' }}>Savings Pool</div>
                      </div>
                    </div>
                    <div className="d-flex gap-3" style={{ fontSize: '0.72rem', color: 'var(--eh-text-mid)' }}>
                      <span>PMPM <strong>{fmtDollar(s.pmpm)}</strong></span>
                      <span>Trend <strong>{s.trend}</strong></span>
                      <span>Net Impact <strong className="text-success">{fmtMillions(s.net)}</strong></span>
                      <span>Gate <strong>{s.gate}</strong></span>
                      <span>Share <strong>{s.share}</strong></span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="card mt-3">
        <div className="card-header fw-semibold">What-If Impact Summary — Base Case</div>
        <div className="card-body p-0">
          <table className="table table-sm mb-0 dash-table">
            <thead><tr><th>Outcome</th><th className="text-end">Value</th><th>Context</th></tr></thead>
            <tbody>
              {data.whatIfImpact.map(r => (
                <tr key={r.label}>
                  <td className="fw-semibold">{r.label}</td>
                  <td className={`text-end fw-semibold${r.pos === true ? ' text-success' : r.pos === false ? ' text-danger' : ''}`}>{r.pos === null ? `${r.value}%` : (r.pos === true ? '+' : '') + fmtMillions(r.value)}</td>
                  <td style={{ fontSize: '0.75rem', color: 'var(--eh-text-mid)' }}>{r.context}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const SUBTABS = [
  { key: 'vbc',        label: 'VBC Dashboard'       },
  { key: 'drilldown',  label: 'Program Drill-Down'  },
  { key: 'control',    label: 'Control Group Eval.' },
  { key: 'contract',   label: 'Contract Setting'    },
];

const VIEW_COMPONENTS = {
  vbc:       VBCDashboardView,
  drilldown: ProgramDrillDownView,
  control:   ControlGroupEvalView,
  contract:  ContractSettingView,
};

export default function PerformanceIntelligence() {
  const [sub, setSub] = useState('vbc');
  const [memberCount, setMemberCount] = useState(null);
  const ViewComponent = VIEW_COMPONENTS[sub];
  useEffect(() => {
    apiFetch('/api/dashboards/intelligence-config')
      .then(r => r.json())
      .then(d => setMemberCount(d.membership_count))
      .catch(() => {});
  }, []);
  return (
    <div className="dash-tab-content">
      <SubTabBar tabs={SUBTABS} active={sub} onChange={setSub} />
      <ViewComponent memberCount={memberCount} />
    </div>
  );
}
